import { Subscription, StringCodec } from "nats.ws";
import { useEffect, useRef } from "react";
import { useNatsConnection } from "@/lib/context/NATS-provider";

/**
 * Registry to store shared subscriptions
 * Key: topic name
 * Value: { subscription, callbacks, refCount }
 */
const subscriptionRegistry: Record<
    string,
    {
        subscription: Subscription;
        callbacks: Array<(message: any) => void>;
        refCount: number;
    }
> = {};

export function useSharedSubscription({ topic, onMessage }: { topic: string, onMessage: (msg: any) => void }) {
    const connection = useNatsConnection();
    const callbackRef = useRef(onMessage);

    // Keep callback ref up-to-date
    useEffect(() => {
        callbackRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        if (!connection) return;

        // If there's no entry for this topic, create one
        if (!subscriptionRegistry[topic]) {
            const sub = connection.subscribe(topic);
            subscriptionRegistry[topic] = {
                subscription: sub,
                callbacks: [],
                refCount: 0,
            };

            // Start reading subscription
            const sc = StringCodec();
            (async () => {
                for await (const m of sub) {
                    const message = JSON.parse(sc.decode(m.data));
                    // Dispatch to all callbacks that are currently registered
                    subscriptionRegistry[topic].callbacks.forEach((cb) => cb(message));
                }
            })();
        }

        // Add this component’s callback
        subscriptionRegistry[topic].refCount++;
        subscriptionRegistry[topic].callbacks.push((msg) => callbackRef.current(msg));

        // Cleanup when unmounting
        return () => {
            subscriptionRegistry[topic].refCount--;
            subscriptionRegistry[topic].callbacks = subscriptionRegistry[topic].callbacks.filter(
                (cb) => cb !== callbackRef.current
            );

            // If no one is using the topic anymore, unsubscribe and remove from registry
            if (subscriptionRegistry[topic].refCount <= 0) {
                subscriptionRegistry[topic].subscription.unsubscribe();
                delete subscriptionRegistry[topic];
            }
        };
    }, [connection, topic]);
}
